import { useCallback, useEffect, useRef, useState } from 'react';

const useTabManager = ({
  workflowData,
  navigate,
  fetchWrapper,
  selectedShape,
  setSelectedShape,
  setCode,
  editorRef,
  isUpdatingEditorRef,
  shapeCodeCacheRef,
  previousSelectedShapeRef,
  previousActiveTabIdRef,
}) => {
  const [tabs, setTabs] = useState([]);
  const [activeTabId, setActiveTabId] = useState(null);
  const [tabCounter, setTabCounter] = useState(0);
  const tabCodeCacheRef = useRef({});
  const workflowDataRef = useRef(workflowData);

  useEffect(() => {
    const activeTab = tabs.find((tab) => tab.id === activeTabId);
    if (activeTab?.workflowData) {
      workflowDataRef.current = activeTab.workflowData;
    } else if (workflowData) {
      workflowDataRef.current = workflowData;
    }
  }, [activeTabId, tabs, workflowData]);

  useEffect(() => {
    if (workflowData && tabs.length === 0 && !activeTabId) {
      const initialTabId = `tab-${tabCounter}`;
      const initialTab = {
        id: initialTabId,
        name: workflowData?.workflow?.filename || workflowData?.workflow?.desc || 'Flowchart',
        fileId: null,
        file: null,
        workflowData: workflowData,
      };
      setTabs([initialTab]);
      setActiveTabId(initialTabId);
      setTabCounter((prev) => prev + 1);
      if (!tabCodeCacheRef.current[initialTabId]) {
        tabCodeCacheRef.current[initialTabId] = {};
      }
    }
  }, [workflowData, tabs.length, activeTabId, tabCounter]);

  const handleFileClick = useCallback(
    async (file) => {
      if (!file.diagrun_id) {
        return;
      }

      const existingTab = tabs.find((tab) => tab.fileId === file.diagrun_id);
      
      if (existingTab) {
        setActiveTabId(existingTab.id);
        return;
      }

      const newTabId = `tab-${tabCounter}`;
      const newTab = {
        id: newTabId,
        name: file.name || 'New Tab',
        fileId: file.diagrun_id,
        file: file,
        workflowData: null,
        isLoading: true,
      };

      setTabs((prev) => [...prev, newTab]);
      setTabCounter((prev) => prev + 1);

      if (!tabCodeCacheRef.current[newTabId]) {
        tabCodeCacheRef.current[newTabId] = {};
      }

      try {
        const processId = process.env.REACT_APP_GET_DIAGRUN;
        
        if (!processId) {
          throw new Error('REACT_APP_GET_DIAGRUN environment variable tanımlı değil!');
        }

        const bodyParams = {
          diagrun_id: file.diagrun_id,
          process: '100023232',
        };
        
        if (!fetchWrapper) {
          throw new Error('fetchWrapper undefined!');
        }
        
        const response = await fetchWrapper(processId, bodyParams, navigate);
        const workflowDataFromResponse = response?.result || response?.workflow || response;
        
        if (workflowDataFromResponse && (workflowDataFromResponse.workflow || workflowDataFromResponse.contents)) {
          setTabs((prev) =>
            prev.map((tab) => 
              tab.id === newTabId 
                ? { ...tab, workflowData: workflowDataFromResponse, isLoading: false } 
                : tab
            ),
          );
          setActiveTabId(newTabId);
        } else {
          setTabs((prev) =>
            prev.map((tab) => 
              tab.id === newTabId 
                ? { ...tab, isLoading: false, error: 'Veri formatı geçersiz' } 
                : tab
            ),
          );
        }
      } catch (error) {
        console.error('File load error:', error.message);
        
        setTabs((prev) =>
          prev.map((tab) => 
            tab.id === newTabId 
              ? { ...tab, isLoading: false, error: error.message || 'Yükleme hatası' } 
              : tab
          ),
        );
      }
    },
    [tabs, tabCounter, navigate, fetchWrapper, setTabs, setTabCounter, setActiveTabId, tabCodeCacheRef],
  );

  const handleTabSwitch = useCallback(
    (tabId) => {
      if (activeTabId && selectedShape) {
        if (!tabCodeCacheRef.current[activeTabId]) {
          tabCodeCacheRef.current[activeTabId] = {};
        }

        const existingCacheValue =
          tabCodeCacheRef.current[activeTabId] && tabCodeCacheRef.current[activeTabId][selectedShape] !== undefined
            ? tabCodeCacheRef.current[activeTabId][selectedShape]
            : null;

        let valueToCache = '';
        if (editorRef.current) {
          try {
            const currentEditorValue = editorRef.current.getValue();
            const editorValue =
              currentEditorValue !== undefined && currentEditorValue !== null ? String(currentEditorValue) : '';

            if (editorValue === '' && existingCacheValue !== null && existingCacheValue !== undefined && existingCacheValue !== '') {
              valueToCache = String(existingCacheValue);
            } else {
              valueToCache = editorValue;
            }
          } catch (error) {
            if (existingCacheValue !== null && existingCacheValue !== undefined) {
              valueToCache = String(existingCacheValue);
            } else {
              valueToCache = '';
            }
          }
        } else if (existingCacheValue !== null && existingCacheValue !== undefined) {
          valueToCache = String(existingCacheValue);
        }

        tabCodeCacheRef.current[activeTabId][selectedShape] = valueToCache;
        shapeCodeCacheRef.current[selectedShape] = valueToCache;
        previousSelectedShapeRef.current = selectedShape;
        previousActiveTabIdRef.current = activeTabId;
      }

      setActiveTabId(tabId);
      setSelectedShape(null);
      setCode('');
      if (editorRef.current) {
        isUpdatingEditorRef.current = true;
        editorRef.current.setValue('');
        setTimeout(() => {
          isUpdatingEditorRef.current = false;
        }, 100);
      }
    },
    [
      activeTabId,
      selectedShape,
      editorRef,
      isUpdatingEditorRef,
      shapeCodeCacheRef,
      previousSelectedShapeRef,
      previousActiveTabIdRef,
      setSelectedShape,
      setCode,
    ],
  );

  const handleTabClose = useCallback(
    (tabId, e) => {
      e.stopPropagation();

      const tabIndex = tabs.findIndex((t) => t.id === tabId);
      if (tabIndex === -1) return;

      if (tabs.length === 1) {
        alert('En az bir tab açık olmalıdır!');
        return;
      }

      const newTabs = tabs.filter((t) => t.id !== tabId);
      setTabs(newTabs);

      if (activeTabId === tabId) {
        const newActiveIndex = tabIndex > 0 ? tabIndex - 1 : 0;
        const newActiveTab = newTabs[newActiveIndex];
        if (newActiveTab) {
          setActiveTabId(newActiveTab.id);
        }
      }
    },
    [tabs, activeTabId],
  );

  return {
    tabs,
    setTabs,
    activeTabId,
    setActiveTabId,
    tabCounter,
    setTabCounter,
    tabCodeCacheRef,
    workflowDataRef,
    handleFileClick,
    handleTabSwitch,
    handleTabClose,
  };
};

export default useTabManager;
